import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import Seo from "@/components/base/Seo";
import ArticleCard from "@/components/feature/ArticleCard";
import {
  formatDate,
  formatViews,
  getArticleById,
  getCategoryName,
  getContentById,
  getRelatedArticles,
} from "@/utils/content";

const shareTargets = [
  { key: "weibo", label: "微博", icon: "ri-weibo-fill" },
  { key: "douyin", label: "抖音", icon: "ri-tiktok-fill" },
  { key: "wechat", label: "微信", icon: "ri-wechat-fill" },
  { key: "bilibili", label: "哔哩哔哩", icon: "ri-play-circle-fill" },
];

export default function ArticleDetail() {
  const { id = "" } = useParams();
  const article = getArticleById(id);
  const [faved, setFaved] = useState(false);
  const [toast, setToast] = useState("");
  const [shareOpen, setShareOpen] = useState(false);

  const showToast = (msg: string) => {
    setToast(msg);
    window.setTimeout(() => setToast(""), 2200);
  };

  if (!article) {
    return (
      <div className="mx-auto flex w-full max-w-[1280px] flex-col items-center px-4 py-24 text-center md:px-6">
        <Seo title="文章不存在 - 噜噜社" description="未找到对应的资讯内容。" />
        <i className="ri-file-unknow-line text-5xl text-foreground-300" />
        <h1 className="mt-4 text-2xl font-bold text-foreground-950">文章不存在或已下线</h1>
        <Link
          to="/"
          className="mt-6 inline-flex whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
        >
          返回首页
        </Link>
      </div>
    );
  }

  const blocks = getContentById(article.id);
  const headings = blocks.filter((b) => b.type === "h2");
  const related = getRelatedArticles(article, 3);
  const categoryName = getCategoryName(article.category);

  return (
    <>
      <Seo
        title={`${article.title} - 噜噜社`}
        description={article.summary.slice(0, 120)}
        keywords={`${article.title},${article.tags.join(",")},噜噜社${categoryName}`}
      />

      {/* 封面 */}
      <section className="relative h-64 w-full overflow-hidden md:h-[420px]">
        <img src={article.cover} alt={article.title} title={`${article.title} - 噜噜社`} className="h-full w-full object-cover object-top" />
        <div className="absolute inset-0 bg-gradient-to-t from-secondary-950/90 via-secondary-950/50 to-secondary-950/20" />
        <div className="absolute inset-0 mx-auto flex w-full max-w-[900px] flex-col justify-end px-4 pb-8 md:px-6">
          <div className="flex flex-wrap items-center gap-2">
            <Link
              to={`/category/${article.category}`}
              className="rounded-full bg-primary-500 px-3 py-1 text-xs font-semibold text-background-50"
            >
              {categoryName}
            </Link>
            {article.tags.map((t) => (
              <Link
                key={t}
                to={`/search?q=${encodeURIComponent(t)}`}
                className="rounded-full border border-background-50/40 px-3 py-1 text-xs text-background-100 transition-colors hover:bg-background-50/10"
              >
                #{t}
              </Link>
            ))}
          </div>
          <h1 className="mt-4 text-2xl font-extrabold leading-snug text-background-50 md:text-4xl">{article.title}</h1>
          <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-background-100/90">
            <span className="flex items-center gap-1.5">
              <i className="ri-user-line" />
              {article.author}
            </span>
            <span className="flex items-center gap-1.5">
              <i className="ri-calendar-line" />
              {formatDate(article.date)}
            </span>
            <span className="flex items-center gap-1.5">
              <i className="ri-eye-line" />
              {formatViews(article.views)} 阅读
            </span>
            <span className="flex items-center gap-1.5">
              <i className="ri-time-line" />
              约 {article.readTime} 分钟
            </span>
          </div>
        </div>
      </section>

      <div className="mx-auto grid w-full max-w-[1100px] grid-cols-1 gap-10 px-4 py-8 md:px-6 lg:grid-cols-[1fr_280px]">
        {/* 正文 */}
        <article className="min-w-0">
          <nav className="flex flex-wrap items-center gap-2 text-sm text-foreground-500">
            <Link to="/" className="transition-colors hover:text-primary-600">
              首页
            </Link>
            <i className="ri-arrow-right-s-line" />
            <Link to={`/category/${article.category}`} className="transition-colors hover:text-primary-600">
              {categoryName}
            </Link>
            <i className="ri-arrow-right-s-line" />
            <span className="line-clamp-1 max-w-[220px] font-medium text-foreground-900">{article.title}</span>
          </nav>

          <p className="mt-6 rounded-lg border-l-4 border-primary-500 bg-primary-50/60 p-4 text-sm leading-relaxed text-foreground-700">
            {article.summary}
          </p>

          {headings.length > 0 && (
            <div className="mt-6 rounded-lg border border-background-200 bg-background-50 p-4">
              <p className="mb-3 flex items-center gap-2 text-sm font-semibold text-foreground-900">
                <i className="ri-list-check-2 text-primary-500" />
                文章目录
              </p>
              <ul className="flex flex-col gap-2">
                {headings.map((h, i) => (
                  <li key={h.text} className="text-sm text-foreground-600">
                    <span className="mr-2 font-medium text-primary-500">0{i + 1}</span>
                    {h.text}
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="mt-8 flex flex-col gap-5">
            {blocks.map((b, i) => {
              if (b.type === "h2") {
                return (
                  <h2 key={`${b.type}-${i}`} className="mt-2 flex items-center gap-3 text-xl font-bold text-foreground-950 md:text-2xl">
                    <span className="h-5 w-1.5 rounded-full bg-primary-500" />
                    {b.text}
                  </h2>
                );
              }
              if (b.type === "quote") {
                return (
                  <blockquote
                    key={`${b.type}-${i}`}
                    className="rounded-lg bg-secondary-100 px-5 py-4 text-sm font-medium italic leading-relaxed text-secondary-900 md:text-base"
                  >
                    {b.text}
                  </blockquote>
                );
              }
              return (
                <p key={`${b.type}-${i}`} className="text-[15px] leading-loose text-foreground-700 md:text-base">
                  {b.text}
                </p>
              );
            })}
          </div>

          {/* 操作区 */}
          <div className="mt-10 flex flex-col gap-4 border-t border-background-200 pt-6 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={() => {
                  setFaved((v) => !v);
                  showToast(faved ? "已取消收藏（演示功能）" : "已加入收藏（演示功能，刷新后重置）");
                }}
                className={`flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md border px-4 py-2 text-sm font-medium transition-colors ${
                  faved
                    ? "border-primary-500 bg-primary-500 text-background-50"
                    : "border-background-300 text-foreground-700 hover:border-primary-400 hover:text-primary-600"
                }`}
              >
                <i className={faved ? "ri-bookmark-fill" : "ri-bookmark-line"} />
                {faved ? "已收藏" : "收藏"}
              </button>
              <div className="relative">
                <button
                  type="button"
                  onClick={() => setShareOpen((v) => !v)}
                  className="flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md border border-background-300 px-4 py-2 text-sm font-medium text-foreground-700 transition-colors hover:border-primary-400 hover:text-primary-600"
                >
                  <i className="ri-share-forward-line" />
                  分享
                </button>
                {shareOpen && (
                  <div className="absolute bottom-full left-0 z-20 mb-2 w-56 rounded-lg border border-background-200 bg-background-50 p-2 shadow-sm">
                    <p className="px-2 py-1.5 text-xs text-foreground-400">分享到（演示：仅复制链接）</p>
                    <div className="grid grid-cols-2 gap-1">
                      {shareTargets.map((s) => (
                        <button
                          key={s.key}
                          type="button"
                          onClick={() => {
                            navigator.clipboard?.writeText(window.location.href).catch(() => undefined);
                            setShareOpen(false);
                            showToast(`已复制链接，可粘贴分享到${s.label}（演示）`);
                          }}
                          className="flex cursor-pointer items-center gap-2 rounded-md px-2 py-2 text-sm text-foreground-700 transition-colors hover:bg-background-100"
                        >
                          <i className={`${s.icon} text-base text-primary-500`} />
                          {s.label}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
            <Link
              to={`/category/${article.category}`}
              className="flex w-fit items-center gap-2 whitespace-nowrap rounded-md bg-secondary-800 px-4 py-2 text-sm font-medium text-background-50 transition-colors hover:bg-secondary-900"
            >
              <i className="ri-arrow-left-line" />
              返回{categoryName}分类
            </Link>
          </div>
        </article>

        {/* 侧栏 */}
        <aside className="flex flex-col gap-6">
          <div className="rounded-lg border border-background-200 bg-background-50 p-4">
            <p className="mb-3 text-sm font-semibold text-foreground-900">关于作者</p>
            <div className="flex items-center gap-3">
              <span className="flex h-11 w-11 items-center justify-center rounded-full bg-primary-100 text-lg font-bold text-primary-700">
                {article.author.slice(0, 1)}
              </span>
              <div>
                <p className="text-sm font-semibold text-foreground-950">{article.author}</p>
                <p className="text-xs text-foreground-500">噜噜社{categoryName}频道编辑</p>
              </div>
            </div>
            <p className="mt-3 text-xs leading-relaxed text-foreground-500">
              关注音乐、影视与潮流文化的长期观察者，相信好的内容值得被认真阅读。
            </p>
          </div>

          <div className="rounded-lg border border-background-200 bg-background-50 p-4">
            <p className="mb-3 flex items-center gap-2 text-sm font-semibold text-foreground-900">
              <i className="ri-price-tag-3-line text-primary-500" />
              本文标签
            </p>
            <div className="flex flex-wrap gap-2">
              {article.tags.map((t) => (
                <Link
                  key={t}
                  to={`/search?q=${encodeURIComponent(t)}`}
                  className="cursor-pointer rounded-full bg-secondary-100 px-3 py-1 text-xs text-secondary-900 transition-colors hover:bg-primary-100 hover:text-primary-800"
                >
                  #{t}
                </Link>
              ))}
            </div>
          </div>
        </aside>
      </div>

      {/* 相关推荐 */}
      <section className="mx-auto w-full max-w-[1100px] px-4 pb-8 md:px-6">
        <div className="mb-5 flex items-center gap-2">
          <span className="h-5 w-1 rounded-full bg-accent-500" />
          <h2 className="text-xl font-bold text-foreground-950">相关内容推荐</h2>
        </div>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {related.map((a) => (
            <ArticleCard key={a.id} article={a} variant="grid" />
          ))}
        </div>
      </section>

      {toast && (
        <div className="fixed bottom-8 left-1/2 z-50 -translate-x-1/2 rounded-full bg-foreground-950 px-5 py-2.5 text-sm text-background-50">
          {toast}
        </div>
      )}
    </>
  );
}